/**
 * Zuora errors that start with [ThreeDs2_Authentication_Exception] aren't always very useful for our users. With these
 * utils we try to make them a little bit more user-friendly.
 *
 * - Most common 3ds2 errors for testing: packages/subscription-management/src/features/credit-card/utils/3dsErrorMessage.ts
 * - Mixpanel: https://mixpanel.com/s/EjRBF
 * - Spreadsheet with initial mapping: https://docs.google.com/spreadsheets/d/1TB8-Xk8NXMXxIQY-lB1a9CF-NGA6IOD-UOYfsQOFZuE/edit#gid=473099859
 */

export interface UserFriendlyError {
  messageTranslationKey?: ErrorTranslationKey;
  userFriendlyZuoraMessage?: string;
}

type ErrorTranslationKey =
  | 'error-contact-bank'
  | 'error-different-card-or-contact-bank'
  | 'error-try-again-or-different-card-or-contact-bank'
  | 'error-generic';

export type Zuora3ds2Error = `[ThreeDs2_Authentication_Exception] ${string}`;

/**
 * Zuora errors follow this pattern: [ThreeDs2_Authentication_Exception] Transaction declined.CODE - error message
 * where CODE maps to the BT authorization decline codes. This function returns that code.
 * https://developer.paypal.com/braintree/articles/control-panel/transactions/declines#authorization-decline-codes
 */
const getErrorCode = (zuora3dsError: Zuora3ds2Error): number | undefined => {
  const errorCode = zuora3dsError.match(/Transaction declined.([0-9]{4})/i);
  return Number.isNaN(errorCode?.[1]) ? undefined : Number(errorCode?.[1]);
};

/**
 * Returns a translation key with a more user-friendly error message, based on the BT authorization decline code.
 * If the zuora3dsError doesn't contain a (matching) code, undefined is returned.
 * https://developer.paypal.com/braintree/articles/control-panel/transactions/declines#authorization-decline-codes
 */
const getMessageTranslationKeyFromErrorCode = (
  zuora3dsError: Zuora3ds2Error,
): ErrorTranslationKey | undefined => {
  const errorCode = getErrorCode(zuora3dsError);

  switch (errorCode) {
    case 2020:
    case 2014:
    case 2038:
    case 2000:
    case 2019:
    case 2007:
    case 2046:
    case 2057:
      return 'error-contact-bank';
    case 2004:
    case 2047:
      return 'error-different-card-or-contact-bank';
    case 2044:
    case 2015:
    case 2002:
    case 2021:
    case 2010:
    case 2099:
    case 2003:
    case 2061:
      return 'error-try-again-or-different-card-or-contact-bank';
    default:
      return undefined;
  }
};

const unusefulZuoraErrors = [
  /Unknown/i,
  /Violation/i,
  /Do Not Honor/i,
  /Processor Declined/i,
  /Transaction Not Allowed/i,
  /Invalid Transaction/i,
  /Declined/i,
  /No Account/i,
  /Invalid Amount/i,
  /Gateway Rejected: risk_threshold/i,
  /Security Violation/i,
  /Invalid Merchant ID/i,
  /Call Issuer. Pick Up Card./i,
  /Invalid Transaction Data/i,
  /Merchant account does not support/i,
];

/**
 * Zuora error might contain useful information to display to user. This returns that user-friendly
 * part of the error message an filters out any unuseful information.
 *
 * @param [ThreeDs2_Authentication_Exception] Transaction declined.2020 - error message
 * @returns error message
 */
const getUserFriendlyZuoraError = (zuora3dsError: Zuora3ds2Error): string | undefined => {
  const userFriendlyRegexMatch = zuora3dsError.match(/- (.*)/);

  if (userFriendlyRegexMatch === null) {
    return undefined;
  }

  const userFriendlyError = userFriendlyRegexMatch[1];
  const isNotUseful = unusefulZuoraErrors.some((unusefulError) =>
    unusefulError.test(userFriendlyError),
  );

  if (isNotUseful) {
    return undefined;
  }

  return userFriendlyError;
};

export const isZuora3ds2Error = (zuoraError: string): zuoraError is Zuora3ds2Error =>
  zuoraError.startsWith('[ThreeDs2_Authentication_Exception] Transaction declined.');

/**
 * Tries to create a more user-friendly error message for zuora errors starting with [ThreeDs2_Authentication_Exception].
 *
 * @param zuora3dsError [ThreeDs2_Authentication_Exception] Transaction declined.2002 - Limit Exceeded
 * @returns {Object} userFriendlyError A more user-friendly 3ds2 error.
 * @returns {string} userFriendlyError.userFriendlyZuoraMessage The user-friendly part of the zuora error, if any.
 * @returns {string} userFriendlyError.messageTranslationKey A translation key for the error message mapped
 * to the BT authorization decline code. A generic message if both the userFriendlyZuoraMessage and maped error message are missing.
 */
export const getUserFriendly3dsError = (
  zuora3dsError: Zuora3ds2Error,
): UserFriendlyError | undefined => {
  if (!isZuora3ds2Error(zuora3dsError)) {
    return undefined;
  }

  const userFriendlyZuoraMessage = getUserFriendlyZuoraError(zuora3dsError);
  const messageTranslationKey = getMessageTranslationKeyFromErrorCode(zuora3dsError);

  if (userFriendlyZuoraMessage === undefined && messageTranslationKey === undefined) {
    return { messageTranslationKey: 'error-generic' };
  }

  return { userFriendlyZuoraMessage, messageTranslationKey };
};
